import React, { useEffect, useState, useCallback } from 'react'
import { Image, Platform, RefreshControl } from 'react-native'
import {
  ArrowLeft, ShoppingBag, Receipt, Store, ChevronRight,
  Package, CreditCard, Banknote, Smartphone, XCircle,
} from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  Button, ButtonText, EmptyState, Spinner, StatCard, Text,
  TopBar, XStack, YStack, ScrollView,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'
import { useLanguage } from '@fliisi/i18n'

interface SaleItem {
  id: string; price: number
  product: { id: string; title: string; title_fi: string | null; product_images: { image_url: string; is_primary: boolean }[] } | null
}
interface SalePayment { payment_method: string; amount: number }
interface Purchase {
  id: string; total: number; subtotal: number; discount_amount: number; receipt_number: string | null
  created_at: string; voided_at: string | null; void_reason: string | null
  flea_market: { id: string; name: string } | null; sale_items: SaleItem[]; sale_payments: SalePayment[]
}
interface DayGroup { date: string; label: string; purchases: Purchase[]; dayTotal: number }
interface Props { navigation: NativeStackNavigationProp<RootStackParamList, 'PurchaseHistory'> }

export default function PurchaseHistoryScreen({ navigation }: Props) {
  const { user } = useAuth()
  const { t } = useLanguage()
  const [purchases, setPurchases] = useState<Purchase[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchPurchases = useCallback(async () => {
    if (!user?.id) return
    try {
      const email = user.email
      let query = supabase.from('sales').select(`
        id, total, subtotal, discount_amount, receipt_number, created_at, voided_at, void_reason,
        flea_market:flea_markets!sales_flea_market_id_fkey(id, name),
        sale_items(id, price, product:products(id, title, title_fi, product_images(image_url, is_primary))),
        sale_payments(payment_method, amount)
      `).order('created_at', { ascending: false }).limit(100)
      query = email ? query.or(`buyer_id.eq.${user.id},buyer_email.eq.${email}`) : query.eq('buyer_id', user.id)
      const { data, error } = await query
      if (error) { console.warn('Purchase query error:', error.message); setPurchases([]); return }
      setPurchases((data as any[]) || [])
    } catch (err) { console.error('Error fetching purchases:', err) }
    finally { setLoading(false); setRefreshing(false) }
  }, [user?.id, user?.email])

  useEffect(() => { fetchPurchases() }, [fetchPurchases])

  const groupByDate = (items: Purchase[]): DayGroup[] => {
    const groups = new Map<string, Purchase[]>()
    for (const purchase of items) {
      const key = new Date(purchase.created_at).toISOString().slice(0, 10)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(purchase)
    }
    const today = new Date().toISOString().slice(0, 10)
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
    return Array.from(groups.entries()).map(([date, items]) => ({
      date, label: date === today ? t('today') : date === yesterday ? t('yesterday') : formatDateFi(date),
      purchases: items, dayTotal: items.filter(p => !p.voided_at).reduce((sum, p) => sum + (p.total || 0), 0),
    }))
  }

  const formatDateFi = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00')
    const days = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La']
    const months = ['tammikuuta', 'helmikuuta', 'maaliskuuta', 'huhtikuuta', 'toukokuuta', 'kesäkuuta', 'heinäkuuta', 'elokuuta', 'syyskuuta', 'lokakuuta', 'marraskuuta', 'joulukuuta']
    return `${days[d.getDay()]} ${d.getDate()}. ${months[d.getMonth()]}`
  }

  const formatTime = (dateStr: string): string => {
    const d = new Date(dateStr)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  const getPaymentIcon = (method: string) => {
    switch (method) { case 'card': return CreditCard; case 'cash': return Banknote; case 'mobilepay': return Smartphone; default: return CreditCard }
  }
  const getPaymentLabel = (method: string) => {
    switch (method) { case 'card': return t('card'); case 'cash': return t('cash'); case 'mobilepay': return t('mobilepay'); default: return method }
  }
  const getProductTitle = (product: SaleItem['product']) => product ? (product.title_fi || product.title || t('unnamed_product')) : t('unknown_product')
  const getProductImage = (product: SaleItem['product']): string | null => {
    if (!product?.product_images?.length) return null
    return (product.product_images.find(img => img.is_primary) || product.product_images[0])?.image_url || null
  }

  const totalSpent = purchases.filter(p => !p.voided_at).reduce((sum, p) => sum + (p.total || 0), 0)
  const dayGroups = groupByDate(purchases)

  if (loading) return <YStack f={1} bg="$background" jc="center" ai="center"><Spinner size="large" /></YStack>

  return (
    <FullScreenLayout>
      <ScrollView f={1} contentContainerStyle={{ maxWidth: 768, alignSelf: 'center', width: '100%', paddingBottom: 16 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchPurchases() }} />}>
        <TopBar title={t('my_purchases')} leftIcon={<ArrowLeft size={22} color="$color" />} onLeftPress={() => navigation.goBack()} />

        {purchases.length === 0 ? (
          <EmptyState
            icon={<ShoppingBag size={48} color="$colorPrimary" />}
            title={t('no_purchases_yet')}
            description={t('purchases_empty_hint')}
            action={<Button variant="primary" onPress={() => navigation.navigate('BuyerHome')}><ButtonText>{t('browse_products')}</ButtonText></Button>}
          />
        ) : (
          <>
            <XStack px={20} gap={10} mb={20}>
              <StatCard f={1} label={t('total')} value={`${totalSpent.toFixed(2)} €`} />
              <StatCard f={1} label={t('purchase_count')} value={String(purchases.filter(p => !p.voided_at).length)} />
              <StatCard f={1} label={t('products')} value={String(purchases.filter(p => !p.voided_at).reduce((sum, p) => sum + (p.sale_items?.length || 0), 0))} />
            </XStack>

            {dayGroups.map(group => (
              <YStack key={group.date} mb={20}>
                <XStack jc="space-between" ai="center" px={20} mb="$sm">
                  <Text fontSize={14} fontWeight="600" color="$colorSecondary" textTransform="uppercase" letterSpacing={0.5}>{group.label}</Text>
                  <Text fontSize={14} fontWeight="600" color="$color">{group.dayTotal.toFixed(2)} €</Text>
                </XStack>

                {group.purchases.map(purchase => {
                  const isVoided = !!purchase.voided_at
                  const expanded = expandedId === purchase.id
                  const payment = purchase.sale_payments?.[0]
                  const PaymentIcon = payment ? getPaymentIcon(payment.payment_method) : Receipt
                  const itemCount = purchase.sale_items?.length || 0

                  return (
                    <YStack key={purchase.id} mx={20} mb="$sm" bg="$backgroundCard" br="$lg" overflow="hidden" opacity={isVoided ? 0.6 : 1} elevation={1}
                      onPress={() => setExpandedId(expanded ? null : purchase.id)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                      <XStack ai="center" p={14} gap="$sm">
                        <YStack w={40} h={40} br={10} bg={isVoided ? 'rgba(239,68,68,0.07)' : 'rgba(74,144,164,0.07)'} jc="center" ai="center">
                          {isVoided ? <XCircle size={20} color="#EF4444" /> : <PaymentIcon size={20} color="$colorPrimary" />}
                        </YStack>
                        <YStack f={1}>
                          <Text fontSize={15} fontWeight="600" color="$color" numberOfLines={1} textDecorationLine={isVoided ? 'line-through' : 'none'}>
                            {(purchase.flea_market as any)?.name || t('store')}
                          </Text>
                          <Text fontSize={12} color="$colorSecondary" mt={2}>
                            {formatTime(purchase.created_at)} · {itemCount} {t('products')}{payment ? ` · ${getPaymentLabel(payment.payment_method)}` : ''}
                          </Text>
                        </YStack>
                        <YStack ai="flex-end" gap={2}>
                          <Text fontSize={isVoided ? 13 : 16} fontWeight={isVoided ? '600' : '700'} color={isVoided ? '$colorError' : '$color'}>
                            {isVoided ? t('voided') : `${purchase.total.toFixed(2)} €`}
                          </Text>
                          <ChevronRight size={16} color="$colorSecondary" style={{ transform: [{ rotate: expanded ? '90deg' : '0deg' }] }} />
                        </YStack>
                      </XStack>

                      {expanded && (
                        <YStack px={14} pb={14} btw={1} btc="$borderColor">
                          {purchase.receipt_number && (
                            <XStack ai="center" gap={6} py="$sm"><Receipt size={14} color="$colorSecondary" /><Text fontSize={12} color="$colorSecondary" fontFamily={Platform.OS === 'ios' ? 'Menlo' : 'monospace'}>{purchase.receipt_number}</Text></XStack>
                          )}
                          {isVoided && purchase.void_reason && (
                            <YStack bg="rgba(239,68,68,0.07)" br="$sm" p={10} mb="$sm"><Text fontSize={13} color="$colorError" fontWeight="500">{t('reason')}: {purchase.void_reason}</Text></YStack>
                          )}
                          {purchase.sale_items?.map(item => {
                            const imgUrl = getProductImage(item.product)
                            return (
                              <XStack key={item.id} ai="center" py="$sm" gap={10}
                                onPress={() => { if (item.product?.id) navigation.navigate('ProductView', { productId: item.product.id }) }}
                                pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer" disabled={!item.product?.id}>
                                {imgUrl ? <Image source={{ uri: imgUrl }} style={{ width: 40, height: 40, borderRadius: 8 }} />
                                  : <YStack w={40} h={40} br="$md" bg="$backgroundSecondary" jc="center" ai="center"><Package size={16} color="#ccc" /></YStack>}
                                <Text f={1} fontSize={14} color="$color" numberOfLines={1}>{getProductTitle(item.product)}</Text>
                                <Text fontSize={14} fontWeight="600" color="$color">{item.price.toFixed(2)} €</Text>
                              </XStack>
                            )
                          })}
                          {purchase.discount_amount > 0 && (
                            <XStack jc="space-between" py={6} btw={1} btc="$borderColor" mt={4}>
                              <Text fontSize={13} color="$colorSecondary">{t('discount')}</Text>
                              <Text fontSize={13} fontWeight="600" color="#4CAF50">-{purchase.discount_amount.toFixed(2)} €</Text>
                            </XStack>
                          )}
                          {(purchase.flea_market as any)?.id && (
                            <XStack ai="center" gap={6} py={10} mt={4} btw={1} btc="$borderColor"
                              onPress={() => navigation.navigate('StoreBrowse', { marketId: (purchase.flea_market as any).id, marketName: (purchase.flea_market as any).name })}
                              pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer">
                              <Store size={16} color="$colorPrimary" />
                              <Text f={1} fontSize={13} fontWeight="500" color="$colorPrimary">{t('show_store')}</Text>
                              <ChevronRight size={14} color="$colorPrimary" />
                            </XStack>
                          )}
                        </YStack>
                      )}
                    </YStack>
                  )
                })}
              </YStack>
            ))}
            <YStack h={32} />
          </>
        )}
      </ScrollView>
    </FullScreenLayout>
  )
}
